import { useEffect, useRef, useState } from 'react';

import scrollUp from '../../assets/gifs/scroll-up.gif';
import mouseScrollDown from '../../assets/gifs/mouse-scroll-down.gif';

import './LandingView.scss';

import { isMobileDevice } from '../../utils/device';
import { formatImageUrl, getLandingPage } from '../../services/content';
import CarouselCard from '../CarouselCard';
import { ILandingPageCard } from '../../interfaces/content/IPage';
import Loading from '../Loading/Loading';

interface LandingViewProps {
  setHeader: (value: string) => void;
}

export default function LandingView({ setHeader }: LandingViewProps) {
  const [steps, setSteps] = useState<ILandingPageCard[]>([]);
  const [currentStep, setCurrentStep] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [isChangingStep, setIsChangingStep] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLastStep, setIsLastStep] = useState(false);

  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const data = steps[currentStep];

  function preloadNextImage() {
    const nextStep = steps[currentStep + 1];
    if (!nextStep?.thumbnail) return;

    const img = new Image();
    img.src = formatImageUrl(nextStep.thumbnail) || '';
  }

  function stopAutoPlay() {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  }

  function startAutoPlay() {
    stopAutoPlay();

    autoPlayRef.current = setInterval(() => {
      if (isChangingStep || isLastStep) return;
      handleStepChange('next');
    }, 5000);
  }

  function handleStepChange(direction?: 'next' | 'previous') {
    stopAutoPlay();

    if (isChangingStep) return;

    const lastIndex = steps.length - 1;

    if (direction === 'next' && currentStep === lastIndex) return;
    if (direction === 'previous' && currentStep === 0) return;

    setIsChangingStep(true);

    setTimeout(() => {
      let newStep = currentStep;

      if (direction === 'next') newStep++;
      if (direction === 'previous') newStep--;

      setIsLastStep(newStep === lastIndex);
      setCurrentStep(newStep);
      setHeader(steps[newStep]?.header);
      setIsChangingStep(false);
    }, 300);
  }

  async function fetchLandingSteps() {
    try {
      const response = await getLandingPage();
      setSteps(response?.cards || []);
      setHeader(response?.cards?.[0]?.header);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  // init
  useEffect(() => {
    fetchLandingSteps();

    function updateSize() {
      setIsMobile(isMobileDevice());
    }

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
      stopAutoPlay();
    };
  }, []);

  // autoplay
  useEffect(() => {
    if (steps.length > 0) {
      startAutoPlay();
    }
  }, [steps]);

  useEffect(() => {
    preloadNextImage();
    startAutoPlay();
  }, [currentStep]);

  // scroll
  useEffect(() => {
    function handleScroll(e: WheelEvent) {
      if (!isChangingStep) {
        handleStepChange(e.deltaY > 0 ? 'next' : 'previous');
      }
    }

    window.addEventListener('wheel', handleScroll);
    return () => window.removeEventListener('wheel', handleScroll);
  }, [isChangingStep, currentStep]);

  // touch
  const handleTouchStart = (e: TouchEvent | any) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: TouchEvent | any) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartY.current || !touchEndY.current) return;

    const delta = touchStartY.current - touchEndY.current;

    if (Math.abs(delta) > 50 && !isChangingStep) {
      handleStepChange(delta > 0 ? 'next' : 'previous');
    }

    touchStartY.current = null;
    touchEndY.current = null;
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Loading />
      </div>
    );
  }

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`overflow ${isLastStep ? 'overflow__silvio-background' : ''}`}
    >
      {steps.length > 0 && (
        <>
          <div className={`box box__${isChangingStep ? 'leave' : 'enter'}`}>
            <CarouselCard
              item={{
                url: '',
                title: data?.title,
                description: data?.description || '',
                icon: data?.icon,
                thumbnail: data?.thumbnail
              }}
              variant="large"
            />
          </div>

          {!isLastStep && (
            <div className="mouse-advice-section">
              <img
                alt="Arrastar para baixo"
                src={isMobile ? scrollUp : mouseScrollDown}
                onClick={() => handleStepChange('next')}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
